// El alcance de un usuario, tal como se lee en la tabla, y el espejo de
// `Usuario.PuedeEn` que la consola necesita para no enseñar un botón que el
// servidor va a rechazar.
//
// Lo primero va aquí y no dentro del .vue porque es la parte que se puede
// probar: el estado «lleva el permiso y no tiene ni un cliente» es el que
// produce la llamada «no veo nada», y merece una prueba que no dependa de
// montar un componente.
//
// Lo segundo -el espejo- existe por el Ruling 5 del plan de permisos por
// tenant: la Task 11 original solo tocaba la pantalla de Usuarios, y con eso
// un líder tendría el permiso por la API (Task 4, Task 9) y la consola le
// seguiría escondiendo los botones de Inteligencia, Versiones y Operación
// -que miran `sesion.puedeEditar`/`puedePublicar`, la lista PLANA de
// permisos, y un líder no lleva `botia-edit` ni `botia-pub` ahí-. La
// funcionalidad habría existido solo para quien supiera usar `curl`.
//
// LA CONSOLA NO DECIDE NADA: el servidor vuelve a comprobar todo esto en cada
// petición (`exigirAlcance` y `exigirEn` en omnibot-edge/internal/consola/
// servidor.go). Si este espejo se desincroniza de `auth.go` el efecto es
// cosmético -un botón de más, que da 403/404 al pulsarlo, o de menos, que
// esconde algo que sí se podía- y no un agujero de seguridad. Aun así tiene
// que ser un espejo EXACTO: un botón que da 403 es un fallo con nombre propio
// en este plan, tanto como uno que abre una puerta cerrada.

export const ALCANCE_TODOS = 'ALL'
export const UID_LIDER = 'botia-leader'

// Los cuatro permisos de la consola, en el mismo orden que
// omnibot-edge/internal/consola/auth.go. No hay un enum compartido con Go
// -son dos lenguajes, dos módulos- así que estas cadenas SON el contrato: si
// alguna vez cambian allá, hay que releer auth.go antes de tocar esto.
export const PERMISO_EDITAR = 'botia-edit'
export const PERMISO_PUBLICAR = 'botia-pub'
export const PERMISO_ADMIN = 'botia-adm'
export const PERMISO_VER = 'botia-ver'

export function esLider(permisos: string[]): boolean {
  return (permisos ?? []).includes(UID_LIDER)
}

// Para PINTAR, no para autorizar: ver el comentario largo de resumenDeAlcance,
// justo abajo, sobre por qué las dos preguntas no son la misma.
export function esAdmin(permisos: string[]): boolean {
  return (permisos ?? []).includes(PERMISO_ADMIN)
}

// Espejo de `Usuario.Puede`: ver está implicado por editar/publicar, y admin
// implica los tres. Privada a este archivo porque ninguna pantalla necesita
// el permiso GLOBAL a secas -toda decisión de botón es sobre un cliente
// (`puedeEn`) o sobre `botia-adm`, que el alcance no toca-.
function puedeGlobal(permisos: string[], permiso: string): boolean {
  const lista = permisos ?? []
  for (const p of lista) {
    if (p === permiso) return true
    if (permiso === PERMISO_VER && (p === PERMISO_EDITAR || p === PERMISO_PUBLICAR)) return true
    if (p === PERMISO_ADMIN) return true
  }
  return false
}

// Espejo de `Usuario.EnAlcance`. El alcance puede llegar `null` desde
// `/api/yo` -así viaja para quien no es líder, ver Task 4- y se trata
// exactamente igual que una lista vacía: un `.includes` sobre `null`
// rompería la pantalla entera, no solo un botón.
export function enAlcance(
  permisos: string[],
  alcance: string[] | null | undefined,
  tenantId: string,
): boolean {
  if (puedeGlobal(permisos, PERMISO_ADMIN)) return true
  if (!esLider(permisos)) return true
  const lista = alcance ?? []
  return lista.includes(ALCANCE_TODOS) || lista.includes(tenantId)
}

// Espejo de `Usuario.PuedeEn`. EL ALCANCE CORTA PRIMERO -es todo el diseño en
// auth.go-: un líder al que además le quedara un permiso global (no debería
// pasar hoy, `PermisoLider` no concede `botia-edit`/`botia-pub` globales,
// pero esta función no lo asume) tampoco entraría a un cliente ajeno.
//
// Lo que concede DENTRO del alcance son los tres permisos de cliente y
// ninguno más: `botia-adm` queda fuera adrede, igual que en el Go, porque
// revelar un secreto es del mismo calibre que publicar el catálogo -un líder
// no lo ve ni en su propio cliente-.
export function puedeEn(
  permisos: string[],
  alcance: string[] | null | undefined,
  permiso: string,
  tenantId: string,
): boolean {
  if (!enAlcance(permisos, alcance, tenantId)) return false
  if (puedeGlobal(permisos, permiso)) return true
  return esLider(permisos) && (permiso === PERMISO_PUBLICAR ||
    permiso === PERMISO_EDITAR || permiso === PERMISO_VER)
}

export interface ResumenAlcance {
  texto: string
  // `ninguno` no es «no tiene clientes»: es «no está acotado». Se pinta en gris
  // y sin etiqueta, porque una etiqueta ahí se lee como algo que alguien
  // concedió.
  tono: 'ninguno' | 'todos' | 'algunos' | 'aviso'
  detalle: string
}

export function resumenDeAlcance(
  permisos: string[],
  alcance: string[] | null | undefined,
): ResumenAlcance {
  // Un admin no esta acotado por NADA, ni siquiera si ademas lleva
  // botia-leader: es lo que dice EnAlcance en el Go
  // (`puedeGlobal(permisos, PermisoAdmin) -> true`, ANTES de mirar si es
  // lider) y lo que dice el parrafo de esta misma pantalla ("a un botia-adm no
  // le recorta nada"). Antes esta funcion miraba solo esLider() -correcto para
  // AUTORIZAR: puedeEn/enAlcance imitan al Go letra por letra y esa
  // implicacion admin-primero ya esta ahi- pero para PINTAR es al reves: un
  // admin con botia-leader se pintaba con un aviso ROJO de "sin clientes" que
  // contradecia el propio parrafo. Encontrado en la segunda lectura
  // adversarial del 12 de septiembre de 2026.
  if (esAdmin(permisos)) {
    return { texto: '—', tono: 'ninguno',
      detalle: 'Lleva botia-adm: no está acotado a ningún cliente, aunque también lleve botia-leader.' }
  }
  if (!esLider(permisos)) {
    return { texto: '—', tono: 'ninguno',
      detalle: 'No lleva el permiso de líder, así que no está acotado a ningún cliente.' }
  }
  const lista = alcance ?? []
  if (lista.includes(ALCANCE_TODOS)) {
    return { texto: 'Todos', tono: 'todos',
      detalle: 'Responde por todos los clientes, incluidos los que se den de alta mañana.' }
  }
  if (lista.length === 0) {
    return { texto: 'sin clientes', tono: 'aviso',
      detalle: 'Lleva botia-leader y no tiene ningún cliente asignado: entra, no ve nada ' +
               'y no hay ningún error que se lo explique.' }
  }
  return { texto: `${lista.length} ${lista.length === 1 ? 'cliente' : 'clientes'}`,
    tono: 'algunos', detalle: lista.join(', ') }
}
